import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, X, Lock } from "lucide-react";

// ⚠️ AJUSTE CE CHEMIN selon l'emplacement réel du composant Admin dans ton projet
// (le back-office hôtelier existant — celui rendu sur la route /admin)
import Admin from "../adminHotel/pages/Admin";

const API = "https://api.komorotel.com/api/superadmin";

// 🆕 CRÉATION TARIFS — les seules créations (POST) autorisées en Mode Vision.
// Doit rester aligné avec ALLOWED_VISION_WRITES côté backend (verifyAdmin),
// qui reste la vraie protection.
const ALLOWED_VISION_CREATES = [
  /\/api\/rateplans\/?(\?.*)?$/,   // créer un tarif
  /\/api\/rateprices\/?(\?.*)?$/,  // créer un prix
];

/**
 * Mode Vision — route /superadmin/vision/:hotelId
 *
 * Le cookie admin_token avec flag isSuperAdminView a déjà été posé par
 * POST /superadmin/hotels/:id/vision avant la navigation vers cette page.
 *
 * 🆕 Côté backend, verifyAdmin autorise désormais une whitelist précise de
 * modifications (PUT hôtel, PUT room, PUT rateplan/rateprice, etc.) mais
 * bloque la création (POST) — 403 sinon — SAUF pour les tarifs (rate plan, prix). La vraie protection
 * reste donc serveur. Côté frontend, on superpose :
 *   1. une bannière persistante "Mode Vision — Modification autorisée, création désactivée"
 *   2. un intercepteur axios qui n'annule plus que les créations (POST)
 *      (les PUT/PATCH passent, et sont filtrés finement par le backend)
 *   3. chaque écriture qui passe est journalisée côté backend (VISION_MODE_EDIT)
 */
export default function VisionMode() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotelName, setHotelName] = useState("");
  const [blockedToast, setBlockedToast] = useState(false);

  // Récupère le nom de l'hôtel pour la bannière
  useEffect(() => {
    axios.get(`${API}/hotels/${hotelId}`, { withCredentials: true })
      .then(r => setHotelName(r.data?.hotel?.name || r.data?.name || ""))
      .catch(() => setHotelName(""));
  }, [hotelId]);

  // 🆕 Intercepteur axios : ne bloque plus que les créations (POST).
  // Les PUT/PATCH passent désormais — verifyAdmin (whitelist) décide au cas par
  // cas et renverra un 403 propre si la route n'est pas autorisée.
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const method = (config.method || "get").toLowerCase();
      // 🆕 CRÉATION TARIFS — la création reste bloquée, sauf pour les tarifs
      const isAllowedCreate = ALLOWED_VISION_CREATES.some(re => re.test(config.url || ""));
      const isCreate = method === "post" && !isAllowedCreate;
      const isAdminApi = (config.url || "").includes("api.komorotel.com");
      const isVisionApi = (config.url || "").includes("/superadmin/");

      if (isCreate && isAdminApi && !isVisionApi) {
        setBlockedToast(true);
        setTimeout(() => setBlockedToast(false), 2500);
        const controller = new AbortController();
        controller.abort();
        return { ...config, signal: controller.signal };
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // 🆕 Si le backend rejette un PUT/PATCH non whitelisté (403), on affiche le
  // même toast plutôt que de laisser une erreur brute remonter dans l'UI Admin.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const isAdminApi = (err.config?.url || "").includes("api.komorotel.com");
        const isVisionApi = (err.config?.url || "").includes("/superadmin/");
        if (err.response?.status === 403 && isAdminApi && !isVisionApi) {
          setBlockedToast(true);
          setTimeout(() => setBlockedToast(false), 2500);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

const handleExit = () => {
    navigate("/superadmin/hotels");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── BANNIÈRE MODE VISION (persistante) ── */}
      <div className="sticky top-0 z-[100] flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-900 text-white shadow-sm">
        <div className="flex items-center gap-2.5 min-w-0">
          <Eye size={16} className="text-[#7BC85F] shrink-0" />
          <p className="text-sm truncate">
            <span className="font-semibold">Mode Vision</span>
            {hotelName && <span className="text-gray-300"> — {hotelName}</span>}
            {/* 🆕 message mis à jour pour refléter le nouveau comportement */}
            <span className="hidden sm:inline text-gray-400"> · Modification autorisée, création limitée aux tarifs · actions journalisées</span>
          </p>
        </div>
        <button
          onClick={handleExit}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium transition shrink-0"
        >
          <X size={13} />
          Quitter
        </button>
      </div>

      {/* ── TOAST "ACTION BLOQUÉE" ── */}
      {blockedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-lg">
          <Lock size={14} className="text-[#7BC85F]" />
          {/* 🆕 message générique car la raison peut être "création" ou "route non whitelistée" */}
          Mode Vision : cette action n'est pas autorisée.
        </div>
      )}

      {/* ── BACK-OFFICE HÔTELIER (UI Admin existante) ── */}
      {/* 🆕 Le voile CSS qui désactivait tous les boutons submit a été retiré :
          il bloquait visuellement des actions que le backend autorise désormais
          (modification). Seule la création reste bloquée, gérée par les
          intercepteurs ci-dessus + le 403 backend, pas par du CSS. */}
      <div className="flex-1">
        <Admin />
      </div>
    </div>
  );
}